import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataNode, EventDataNode, Key } from 'rc-tree/lib/interface';
import {
    useGetResourceLabel,
    useTranslate,
    useResourceContext,
    Identifier,
    useNotify,
    MutationMode,
} from 'react-admin';
import cloneDeep from 'lodash/cloneDeep';

import {
    useGetChildNodesCallback,
    useGetRootNodes,
    useGetTree,
    useGetTreeCallback,
    useMoveAsNthChildOf,
    useMoveAsNthSiblingOf,
} from '../dataProvider';
import { getRCTree, useExpandedKeys } from '../util';
import { TreeRecord } from '../types';
import { UNSAVED_NEW_NODE } from '../constants';

export const useTreeController = <RecordType extends TreeRecord = TreeRecord>(
    options: UseTreeControllerOptions
): UseTreeControllerResult<RecordType> => {
    const translate = useTranslate();
    const resource = useResourceContext(options);
    const getResourceLabel = useGetResourceLabel();
    const {
        hideRootNodes,
        lazy = false,
        titleField = 'title',
        mutationMode = 'undoable',
        meta,
    } = options;

    // Node creation support
    const [newNode, setNewNode] = useState<
        TemporaryNewNode<RecordType> | undefined
    >();

    const handleAddNode = useCallback(
        (newNode: TemporaryNewNode<RecordType>) => {
            setNewNode(newNode);
        },
        []
    );

    // load entire tree on mount if lazy loading is not enabled
    const treeState = useGetTree<RecordType>(
        resource,
        { meta },
        { enabled: !lazy }
    );

    // load only the root nodes on mount if lazy loading is enabled
    const rootNodesState = useGetRootNodes<RecordType>(
        resource,
        { meta },
        { enabled: lazy }
    );

    const innerLoadChildNodes = useGetChildNodesCallback<RecordType>(resource, {
        meta,
    });

    const { data, error, isLoading, isFetching } = lazy
        ? rootNodesState
        : treeState;

    const [finalData, setFinalData] = useState<RecordType[]>(data);

    const loadChildNodes = useCallback(
        async (parentId: RecordType['id']) => {
            const records = await innerLoadChildNodes(parentId);
            setFinalData(previous => [...previous, ...records]);
        },
        [innerLoadChildNodes]
    );

    useEffect(() => {
        const hasNewNode =
            newNode != null &&
            !data.some(record => record.id === UNSAVED_NEW_NODE);

        if (hasNewNode) {
            const newNodes = cloneDeep(data);
            if (newNode.parentId != null) {
                const parentNode = newNodes.find(
                    node => node.id === newNode.parentId
                );
                if (parentNode) {
                    parentNode.children = parentNode.children || [];
                    parentNode.children.push(UNSAVED_NEW_NODE);
                }
            }
            setFinalData([
                // @ts-ignore
                ...newNodes,
                // @ts-ignore
                { id: UNSAVED_NEW_NODE, children: [], ...newNode.record },
            ]);
        } else {
            setFinalData(data);
        }
    }, [newNode, data]);

    // prepare data structure for rc-tree
    // add key and title, and turn to recursive DataNode[] format
    const tree = useMemo(
        () =>
            finalData
                ? getRCTree<RecordType>(
                      finalData,
                      titleField,
                      translate('ra-tree.new_node'),
                      hideRootNodes
                  )
                : null,
        [finalData, hideRootNodes, titleField, translate]
    );

    const [expandedKeys, handleExpand] = useExpandedKeys();
    const expandedNodesLoaded = useRef(false);
    // for some reason, rc-tree does not call loadData on all the expanded nodes in lazy mode
    // so we need to do it manually
    useEffect(() => {
        if (lazy && !expandedNodesLoaded.current) {
            expandedKeys.forEach(expandedKey => {
                loadChildNodes(expandedKey);
            });
            expandedNodesLoaded.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedKeys, lazy]);

    // prepare hooks for drag and drop
    const [moveAsNthChildOf] = useMoveAsNthChildOf(resource, { meta });
    const [moveAsNthSiblingOf] = useMoveAsNthSiblingOf(resource, { meta });
    const refreshTree = useGetTreeCallback(resource);
    const notify = useNotify();

    /**
     * Call dataProvider.moveXXX() method on drop.
     *
     * Heavily inspired by https://github.com/react-component/tree/blob/master/examples/draggable.jsx
     *
     * @see https://github.com/react-component/tree/blob/master/examples/draggable.jsx
     */
    const handleDrop = useCallback(
        (info: any): void => {
            const dropKey = info.node.key;
            const dragKey = info.dragNode.key;
            const dropPos = info.node.pos.split('-');
            const dropPosition =
                info.dropPosition - Number(dropPos[dropPos.length - 1]);

            const dataById = data.reduce((acc, curr) => {
                acc[curr.id] = curr;
                return acc;
            }, {});

            const dragObject = dataById[dragKey];
            const targetObject = dataById[dropKey];

            // refresh tree on success
            const successSideEffect = async (): Promise<void> => {
                await refreshTree();
                notify('ra-tree.item_moved', {
                    undoable: mutationMode === 'undoable',
                });
            };

            if (!info.dropToGap) {
                // Drop on a node
                // move dragObject as first child of targetObject
                moveAsNthChildOf(
                    resource,
                    {
                        source: dragObject,
                        destination: targetObject,
                        position: 0,
                    },
                    { onSuccess: successSideEffect, mutationMode }
                );
            } else if (
                (info.node.children || []).length > 0 && // Has children
                info.node.expanded && // Is expanded
                dropPosition === 1 // On the bottom gap
            ) {
                // drop on the top gap
                // move dragObject as last child of targetObject
                moveAsNthChildOf(
                    resource,
                    {
                        source: dragObject,
                        destination: targetObject,
                        position: targetObject.children.length,
                    },
                    { onSuccess: successSideEffect, mutationMode }
                );
            } else {
                // drop between nodes
                // look for the target position
                let targetPosition;
                const loop = (nodes, key, callback): void => {
                    nodes.forEach((item, index) => {
                        if (item.key === key) {
                            callback(index);
                            return;
                        }
                        if (item.children) {
                            loop(item.children, key, callback);
                        }
                    });
                };
                loop(tree, dropKey, index => {
                    targetPosition = index;
                });

                // move dragObject to sibling targetPosition of targetObject
                moveAsNthSiblingOf(
                    resource,
                    {
                        source: dragObject,
                        destination: targetObject,
                        position:
                            dropPosition === -1
                                ? targetPosition
                                : targetPosition + 1,
                    },
                    { onSuccess: successSideEffect, mutationMode }
                );
            }
        },
        [
            data,
            moveAsNthChildOf,
            moveAsNthSiblingOf,
            mutationMode,
            notify,
            refreshTree,
            resource,
            tree,
        ]
    );

    const defaultTitle = translate('ra.page.list', {
        name: getResourceLabel(resource, 2),
    });

    return {
        data: finalData,
        defaultTitle,
        error,
        expandedKeys,
        handleAddNode,
        handleDrop,
        handleExpand,
        isLoading,
        isFetching,
        loadChildNodes,
        newNode,
        tree,
    };
};

export interface UseTreeControllerResult<
    RecordType extends TreeRecord = TreeRecord
> {
    data: RecordType[];
    defaultTitle: string;
    error?: any;
    expandedKeys: Identifier[];
    handleAddNode: (newNode: TemporaryNewNode<RecordType>) => void;
    handleExpand: (expandedKeys: Identifier[]) => void;
    handleDrop: (info: {
        event: React.MouseEvent;
        node: EventDataNode<TreeRecord>;
        dragNode: EventDataNode<TreeRecord>;
        dragNodesKeys: Key[];
        dropPosition: number;
        dropToGap: boolean;
    }) => void;
    isLoading: boolean;
    isFetching: boolean;
    loadChildNodes: (parentId: RecordType['id']) => void;
    newNode?: TemporaryNewNode;
    tree: DataNode[];
}

export interface UseTreeControllerOptions {
    hideRootNodes?: boolean;
    lazy?: boolean;
    resource: string;
    titleField: string;
    mutationMode?: MutationMode;
    meta?: any;
}

export interface TemporaryNewNode<RecordType extends TreeRecord = TreeRecord> {
    record: Partial<RecordType>;
    parentId: RecordType['id'];
}
